import { useId, useRef } from "react";
import { type ContextMenuItem, interactiveIndexes, isInteractive, isItemDisabled, isSubmenu } from "./items";
import { handleMenuKey } from "./menuKeyboard";
import { MenuRow } from "./MenuRow";
import { type InitialFocus, useInitialFocus } from "./useInitialFocus";
import { useItemFocus } from "./useItemFocus";
import type { MenuCloser } from "./useMenuCloser";
import { type PlacePanel, submenuPlacement, usePanelPosition } from "./usePanelPosition";
import { useSubmenu } from "./useSubmenu";

/** Where a panel sits until it has been measured: off screen and hidden. */
const UNPLACED_OFFSET = -9999;

export interface MenuPanelProps {
  items: ContextMenuItem[];
  /** 0 for the root panel, one more per submenu. */
  level: number;
  ariaLabel: string;
  id?: string;
  place: PlacePanel;
  closer: MenuCloser;
  initialFocus: InitialFocus;
  /** ← pressed in this panel. */
  onBack: () => void;
  /** Escape pressed in this panel. */
  onEscape: () => void;
  onPointerEnter?: () => void;
}

/** One level of the menu, with its open submenu (if any) rendered next to it. */
export function MenuPanel({ items, level, ariaLabel, id, place, closer, initialFocus, onBack, onEscape, onPointerEnter }: MenuPanelProps): React.JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);
  const rows = useItemFocus();
  const submenu = useSubmenu();
  const subId = useId();
  const position = usePanelPosition(panelRef, place);
  useInitialFocus(position !== undefined, initialFocus, { items, panelRef, element: rows.element });
  const interactive = interactiveIndexes(items);
  const openSub = submenu.openSub;

  function activate(index: number, submenuFocus: InitialFocus): void {
    const item = items[index];
    if (!item || !isInteractive(item) || isItemDisabled(item)) return;
    if (item.kind === "submenu") {
      submenu.cancelHover();
      submenu.open(index, submenuFocus);
      return;
    }
    if (item.kind === "checkbox") {
      item.onSelect();
      return;
    }
    closer.closeAndReturnFocus();
    item.onSelect();
  }

  function closeSubmenu(index: number): void {
    submenu.close();
    rows.focusItem(index);
  }

  function renderEntry(item: ContextMenuItem, i: number): React.JSX.Element {
    if (item === "separator") return <hr key={`sep-${i}`} className="nb-menu-sep" />;
    if (item.kind === "heading")
      return (
        <div key={`h-${i}`} role="presentation" className="nb-menu-label">
          {item.label}
        </div>
      );
    return (
      <MenuRow
        key={`${item.label}-${i}`}
        item={item}
        ref={rows.refFor(i)}
        expanded={openSub?.index === i}
        controls={openSub?.index === i ? subId : undefined}
        onActivate={() => activate(i, "keep")}
        onHover={() => submenu.hover(i, item)}
      />
    );
  }

  const sub = openSub !== null ? items[openSub.index] : undefined;

  return (
    <>
      <div
        ref={panelRef}
        id={id}
        role="menu"
        aria-label={ariaLabel}
        aria-orientation="vertical"
        tabIndex={-1}
        className={`nb-popover nb-ctx${level > 0 ? " is-sub" : ""}`}
        style={{ left: position?.left ?? UNPLACED_OFFSET, top: position?.top ?? UNPLACED_OFFSET, visibility: position ? "visible" : "hidden" }}
        onKeyDown={(e) =>
          handleMenuKey(e, {
            items,
            interactive,
            focusedPosition: () => rows.focusedPosition(interactive),
            focusItem: rows.focusItem,
            activate,
            openSubmenu: (index) => submenu.open(index, "first-item"),
            onBack,
            onEscape,
            closeAndReturnFocus: closer.closeAndReturnFocus,
          })
        }
        onPointerEnter={onPointerEnter}
      >
        {items.map(renderEntry)}
      </div>
      {openSub && isSubmenu(sub) ? (
        <MenuPanel
          key={`${openSub.index}-${openSub.initialFocus}`}
          id={subId}
          items={sub.items}
          level={level + 1}
          ariaLabel={sub.label}
          place={submenuPlacement(
            () => rows.element(openSub.index),
            () => panelRef.current,
          )}
          closer={closer}
          initialFocus={openSub.initialFocus}
          onPointerEnter={submenu.cancelHover}
          onBack={() => closeSubmenu(openSub.index)}
          onEscape={() => closeSubmenu(openSub.index)}
        />
      ) : null}
    </>
  );
}
